import { useCallback, useEffect, useState } from 'react';

import DefaultPreview from '../assets/preview_.png';
import AxiosFileSender from '../lib/AxiosFileSender.js';
import cleanForURL from '../lib/CleanForURL.js';
import AxiosInstance from './AxiosInstance.js';
import useAPIList from './useAPIList.js';

export function asForm(course) {
  return {
    ...course,
    date: course.date.split('T')[0],
    endDate: course.endDate.split('T')[0],
  };
}

export function getURL(course) {
  return `/courses/${cleanForURL(course.name)}/${course.id}`;
}

export function asFigure(course) {
  const date = new Date(course.date);
  const language = localStorage.getItem('language') || 'en';

  let monthName = '';
  try {
    date.toLocaleString(language, { month: 'long' });
  } catch (e) {
    monthName = date.toLocaleString('en', { month: 'long' });
  }
  const formattedDate =
    monthName.charAt(0).toUpperCase() +
    monthName.slice(1) +
    ' ' +
    date.getFullYear();

  return {
    id: course.id,
    image: course.image,
    title: course.name,
    location: course.location,
    date: formattedDate,
    link: getURL(course),
  };
}

export function asArticle(course) {
  return {
    id: course.id,
    title: course.name,
    thumbnail: course.image,
    date: course.date,
    year: course.date ? new Date(course.date).getFullYear() : null,
  };
}

// ---

export function useCourses(showAll = false) {
  return useAPIList(`/api/courses?showAll=${showAll}`);
}

export function useCoursesMultiYear() {
  const [courses, setCourses] = useState({});
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const startYear = 2020;

  useEffect(() => {
    const fetchAllYears = async () => {
      setLoading(true);
      const mergedCourses = {};

      const yearPromises = [];
      for (let year = currentYear; year >= startYear; year--) {
        yearPromises.push(
          AxiosInstance.get(`/api/courses?showAll=true&showYear=${year}`)
            .then((res) => ({ year, data: res.data }))
            .catch(() => ({ year, data: {} }))
        );
      }

      const results = await Promise.all(yearPromises);

      for (const { data } of results) {
        for (const language in data) {
          if (!mergedCourses[language]) {
            mergedCourses[language] = [];
          }
          const existingIds = new Set(mergedCourses[language].map((c) => c.id));
          const newCourses = data[language].filter(
            (c) => !existingIds.has(c.id)
          );
          mergedCourses[language].push(...newCourses);
        }
      }

      setCourses(mergedCourses);
      setLoading(false);
    };

    fetchAllYears();
  }, []);

  const deleteItem = useCallback((id) => {
    return AxiosInstance.delete(`/api/courses/${id}`).then((res) => {
      setCourses((prevCourses) => {
        const updated = {};
        for (const key in prevCourses) {
          if (Array.isArray(prevCourses[key])) {
            updated[key] = prevCourses[key].filter((c) => c.id !== id);
          } else {
            updated[key] = prevCourses[key];
          }
        }
        return updated;
      });
      return res.data;
    });
  }, []);

  return [courses, loading, deleteItem];
}

export function useCourse(id, onlyCourse = false) {
  const [course, setCourse] = useState({
    id: id,
    name: 'Loading...',
    image: DefaultPreview,
    program: [],
    date: new Date(),
    endDate: new Date(),
    loading: true,
  });

  const [stands, setStands] = useState([]);
  const [committee, setCommittee] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    fetchCourse(id).then(setCourse).catch(console.error);

    if (onlyCourse) return;
    fetchStands(id).then(setStands).catch(console.error);
    fetchSessions(id).then(setSessions).catch(console.error);
    fetchCommittee(id).then(setCommittee).catch(console.error);
    fetchPermissions(id).then(setPermissions).catch(console.error);
  }, [id, onlyCourse]);

  return [course, sessions, stands, committee, permissions];
}

export function usePermissions(id) {
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    fetchPermissions(id).then(setPermissions).catch(console.error);
  }, [id]);

  return [permissions];
}

export function fetchPermissions(id) {
  return AxiosInstance.get(`/api/courses/${id}/permissions`).then((res) => {
    return res.data;
  });
}

export function fetchCourses(highlight = false) {
  return AxiosInstance.get(`/api/courses?highlight=${highlight}`).then(
    (res) => res.data
  );
}

export function fetchAllCourses() {
  return AxiosInstance.get('/api/courses?showAll=true').then((res) => res.data);
}

export function fetchMyCourses() {
  return AxiosInstance.get('/api/courses/my').then((res) => res.data);
}

export function fetchNextCourses() {
  return fetchCourses().then((courses) =>
    courses.filter((course) => {
      const limitDate = new Date();
      limitDate.setDate(limitDate.getDate() - 1);
      return new Date(course.endDate) > limitDate;
    })
  );
}

export function fetchCoursesAvailable() {
  return AxiosInstance.get('/api/courses/exist-for-language').then(
    (res) => res.data
  );
}

export function addCourse(body) {
  return AxiosFileSender.post('/api/courses', body).then((res) => res.data);
}

export function fetchCourse(id) {
  return AxiosInstance.get(`/api/courses/${id}`).then((res) => res.data);
}

export function updateCourse(id, body) {
  return AxiosFileSender.put(`/api/courses/${id}`, body).then(
    (res) => res.data
  );
}

export function deleteCourse(id) {
  return AxiosInstance.delete(`/api/courses/${id}`).then((res) => res.data);
}

// Sessions
export function useSessions(courseId) {
  return useAPIList(`/api/courses/${courseId}/sessions`);
}

export function fetchSessions(courseId) {
  return AxiosInstance.get(`/api/courses/${courseId}/sessions`).then(
    (res) => res.data
  );
}

// Enrollments
export function enrollCourse(courseId) {
  return AxiosInstance.post(`/api/courses/${courseId}/enrollment`);
}

export function useEnrollments(courseId) {
  return useAPIList(`/api/courses/${courseId}/enrollment`);
}

export function toggleCertificate(courseId, userId, enable) {
  return AxiosInstance.put(
    `/api/courses/${courseId}/enrollment/${userId}/certificate`,
    {
      enable,
    }
  ).then((res) => res.data);
}

// Specialties
export function useSpecialties(courseId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/courses/${courseId}/specialties`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

// Commitee
export function useCommittee(courseId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/courses/${courseId}/committee`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

export function fetchCommittee(courseId) {
  return AxiosInstance.get(`/api/courses/${courseId}/committee`).then(
    (res) => res.data
  );
}

// Stands
export function useStands(courseId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/courses/${courseId}/stands`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

export function fetchStands(courseId) {
  return AxiosInstance.get(`/api/courses/${courseId}/stands`).then(
    (res) => res.data
  );
}

export function fetchEnrollments(courseId, userId) {
  return AxiosInstance.get(`/api/courses/${courseId}/enrollment/find`).then(
    (res) => res.data
  );
}

//Tags
export function useCourseTags(courseId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/courses/${courseId}/tags`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

//Target Audience
export function useCourseTargetAudience(courseId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/courses/${courseId}/target-audience`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

export function fetchCourseUsedTargetAudience() {
  return AxiosInstance.get(`/api/courses/target-audience`).then(
    (res) => res.data
  );
}

export function useCourseUsedTargetAudience(translator) {
  const [targetAudience, setTargetAudience] = useState([]);
  useEffect(() => {
    fetchCourseUsedTargetAudience()
      .then((data) => {
        data.push({
          id: 0,
          name: translator('courses.all_courses_target_audience'),
        });

        data.sort((a, b) => a.id - b.id);

        setTargetAudience(data);
      })

      .catch(console.error);
  }, []);

  return [targetAudience];
}

export function addCourseQuestion(courseId, body) {
  return AxiosFileSender.post(`/api/courses/${courseId}/questions`, body).then(
    (res) => res.data
  );
}
